import { Bar, CartesianGrid, ComposedChart, Customized, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartLayout } from './EmissionFactorModal.styles';
import { EmptyState, EmptyStateText } from '../../../shared/styles/components/primitives.styles';
import { FEEDSTOCKS, FEEDSTOCK_COLORS, FeedstockKey, MetricKey } from '../types/emission-factor.constants';

import { AxisBreakGap, AxisBreakMarks, buildScaleMapping } from './axisBreakUtils';
import React, { useMemo } from 'react';
import { YearlyEmissionValues } from '../types/emission-factor.api';
import { formatNumber } from '../../../shared/utils/formatters.utils';
import { theme } from '../../../shared/styles/core/theme';
import { toPercent } from '../utils/emission-factor.utils';
import { tokenPxToNumber, tokens } from '../../../shared/styles/core/tokens';

interface EmissionFactorChartsProps {
    yearlyValues: YearlyEmissionValues[];
    visibleFeedstocks: Set<FeedstockKey>;
    visibleMetrics: Set<MetricKey>;
}

const renderAxisBreak = (gaps: AxisBreakGap[]) =>
    gaps.length > 0 && (
        <Customized
            component={(props: Record<string, unknown>) => (
                <AxisBreakMarks
                    xAxisMap={props.xAxisMap as any}
                    offset={props.offset as any}
                    gaps={gaps}
                />
            )}
        />
    );

export default function EmissionFactorCharts({ yearlyValues, visibleFeedstocks, visibleMetrics }: EmissionFactorChartsProps) {
    const { scaleMapping, chartData, formatXTick, formatTooltipLabel } = useMemo(() => {
        const sortedValues = [...yearlyValues].sort((a, b) => a.year - b.year);
        const sortedYears = sortedValues.map((yv) => yv.year);
        const scaleMapping = buildScaleMapping(sortedYears);

        const chartData = sortedValues.map((yv) => {
            const point: Record<string, number | null> = {
                _displayX: scaleMapping.toDisplay.get(yv.year) ?? yv.year,
                year: yv.year,
            };
            FEEDSTOCKS.forEach(({ key }) => {
                if (visibleFeedstocks.has(key)) {
                    point[`${key}_emission`] = yv[key]?.emission ?? null;
                    point[`${key}_share`] = toPercent(yv[key]?.share ?? null);
                }
            });
            return point;
        });

        const formatXTick = (displayValue: number): string => {
            const realYear = scaleMapping.toReal.get(displayValue);
            return realYear != null ? String(realYear) : String(displayValue);
        };

        const formatTooltipLabel = (displayValue: unknown): string => {
            const realYear = scaleMapping.toReal.get(displayValue as number);
            return `Year: ${realYear ?? displayValue}`;
        };

        return { scaleMapping, chartData, formatXTick, formatTooltipLabel };
    }, [yearlyValues, visibleFeedstocks]);

    if (!yearlyValues.length) {
        return (
            <EmptyState>
                <EmptyStateText>No data to chart</EmptyStateText>
            </EmptyState>
        );
    }

    const visibleEntries = FEEDSTOCKS.filter(({ key }) => visibleFeedstocks.has(key));
    const showShare = visibleMetrics.has('share');
    const showEmission = visibleMetrics.has('emission');

    const tooltipFormatter = (value: unknown, name: unknown): [string, string] => {
        if (typeof value !== 'number') return ['', String(name ?? '')];
        const label = String(name ?? '');
        if (label.endsWith('%')) {
            return [`${Math.round(value)}%`, label.slice(0, -2)];
        }
        return [formatNumber(value), label];
    };

    return (
        <ChartLayout>
            <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border.subtle} />
                    <XAxis
                        dataKey="_displayX"
                        type="number"
                        domain={[(dataMin: number) => dataMin - 0.5, (dataMax: number) => dataMax + 0.5]}
                        ticks={scaleMapping.displayTicks}
                        allowDecimals={false}
                        tickFormatter={formatXTick}
                        tick={{ fontSize: tokens.font.size.sm, fill: theme.colors.text.secondary }}
                        tickLine={false}
                    />

                    {showShare && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: tokens.font.size.sm, fill: theme.colors.text.secondary }}
                            tickFormatter={(v) => `${v}%`}
                            tickLine={false}
                            domain={[0, 100]}
                            label={{
                                value: 'Share (%)',
                                angle: 90,
                                position: 'insideRight',
                                style: { fontSize: tokens.font.size.sm, fill: theme.colors.text.tertiary },
                            }}
                        />
                    )}

                    {showEmission && (
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: tokens.font.size.sm, fill: theme.colors.text.secondary }}
                            tickFormatter={(v) => String(Math.round(v * 100) / 100)}
                            tickLine={false}
                            label={{
                                value: 'EF',
                                angle: -90,
                                position: 'insideLeft',
                                style: { fontSize: tokens.font.size.sm, fill: theme.colors.text.tertiary },
                            }}
                        />
                    )}

                    <Tooltip formatter={tooltipFormatter} labelFormatter={formatTooltipLabel} />

                    <Legend
                        verticalAlign="bottom"
                        height={tokenPxToNumber(tokens.space.xl)}
                        wrapperStyle={{ paddingTop: tokens.space.md }}
                    />

                    {showShare && visibleEntries.map(({ key, label }, idx, arr) => (
                        <Bar
                            yAxisId="right"
                            key={`bar-${key}`}
                            dataKey={`${key}_share`}
                            name={`${label} %`}
                            fill={FEEDSTOCK_COLORS[key]}
                            fillOpacity={0.6}
                            stackId="share"
                            maxBarSize={50}
                            radius={idx === arr.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        />
                    ))}

                    {showEmission && visibleEntries.map(({ key, label }) => (
                        <Line
                            yAxisId="left"
                            key={`line-${key}`}
                            dataKey={`${key}_emission`}
                            name={`${label} EF`}
                            stroke={FEEDSTOCK_COLORS[key]}
                            strokeWidth={2}
                            dot={{ r: 3, fill: FEEDSTOCK_COLORS[key] }}
                            activeDot={{ r: 5 }}
                        />
                    ))}

                    {renderAxisBreak(scaleMapping.gaps)}
                </ComposedChart>
            </ResponsiveContainer>
        </ChartLayout>
    );
}